import React from 'react'
import { observer } from 'mobx-react-lite'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import FormGroup from '@mui/material/FormGroup'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'

import { Icon, Text } from '@mxenabled/mxui'

import type { Transaction } from '../../../../core'

import {
  ANALYTICS_EVENTS,
  useEvent,
  useGlobalCopyStore,
  useTransactionStore,
} from '../../../../common'
import { useAriaLive } from '../../../../common/hooks/useAriaLive'
import { ON_CLICK_EVENTS } from '../../../constants/OnClickEvents'

interface DescriptionProps {
  transaction: Transaction
  viewOnly?: boolean
}

const Description: React.FC<DescriptionProps> = ({ transaction, viewOnly = false }) => {
  const { updateTransaction } = useTransactionStore()
  const { common: commonCopy, transactions: copy } = useGlobalCopyStore()
  const { onEvent } = useEvent()
  const { announce, ariaLive } = useAriaLive()

  const [isEditing, setIsEditing] = React.useState(false)
  const [description, setDescription] = React.useState('')

  const inputRef = React.useRef<HTMLInputElement>(null)

  React.useEffect(() => {
    if (isEditing) {
      setDescription(transaction.description)
      inputRef?.current?.focus()
    }
  }, [isEditing])

  const handleEdit = () => {
    setIsEditing(true)
    onEvent(ON_CLICK_EVENTS.ON_TRANSACTION_DESCRIPTION_EDIT_CLICK)
    setTimeout(() => document.getElementById(`transaction-description-input`)?.focus(), 0)
  }

  const handleSave = async () => {
    onEvent(ANALYTICS_EVENTS.TRANSACTION_DETAILS_CLICK_DESCRIPTION, {
      transaction_guid: transaction.guid,
    })
    await updateTransaction({ ...transaction, description })
    setIsEditing(false)
    announce(`${copy.payee_title}: ${description}`)
    setTimeout(() => document.getElementById(`transaction-description-edit-button`)?.focus(), 0)
  }

  return (
    <Box className="mx-txn-transaction-description">
      {ariaLive}
      {isEditing ? (
        <FormGroup row={true}>
          <TextField
            aria-label={copy.payee_title}
            id={`transaction-description-input`}
            label={copy.payee_title}
            name={copy.payee_title}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDescription(e.target.value)}
            ref={inputRef}
            slotProps={{ htmlInput: { maxLength: 140 } }}
            sx={{
              backgroundColor: 'background.paper',
              '.MuiOutlinedInput-root': { borderTopRightRadius: 0, borderBottomRightRadius: 0 },
              '.MuiOutlinedInput-input': { p: 11 },
              width: 250,
            }}
            value={description}
          />
          <Button
            disabled={!description}
            onClick={handleSave}
            sx={{
              borderTopLeftRadius: 0,
              borderBottomLeftRadius: 0,
            }}
            variant="outlined"
          >
            {commonCopy.save_button}
          </Button>
        </FormGroup>
      ) : (
        <Stack alignItems="center" direction="row" gap={4} sx={{ minHeight: 44 }}>
          <Text bold={true}>{transaction.description}</Text>
          {!viewOnly && (
            <IconButton
              aria-label={copy.payee_edit}
              id={`transaction-description-edit-button`}
              onClick={handleEdit}
              sx={{ color: 'action.active' }}
            >
              <Icon name="create" size={22} />
            </IconButton>
          )}
        </Stack>
      )}
    </Box>
  )
}

export default observer(Description)
